import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Import components
import ModalHeader from './components/ModalHeader';
import TabNavigation from './components/TabNavigation';
import RouterFeaturesContent from './components/RouterFeatures/RouterFeaturesContent';
import ISPFeaturesContent from './components/ISPFeatures/ISPFeaturesContent';
import MeshSystemContent from './components/MeshSystem/MeshSystemContent';

/**
 * RouterISPFeaturesModal component
 * Modal with three tabs: Router Features, ISP Service Features, and Mesh Technology
 * Positioned below the main navigation
 */
const RouterISPFeaturesModal = ({
  isOpen,
  onClose,
  activeTab: initialActiveTab = 'router' // Accept activeTab prop with default value
}) => {
  const [activeTab, setActiveTab] = useState(initialActiveTab); // 'router', 'isp', or 'mesh'
  
  // Update activeTab when initialActiveTab prop changes
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialActiveTab);
    }
  }, [initialActiveTab, isOpen]);
  
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  // Check if we're on mobile
  const isMobile = windowWidth < 768;
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Prevent body scrolling when modal is open, but allow modal content to scroll
  useEffect(() => {
    if (isOpen) {
      // Save the current scroll position
      const scrollY = window.scrollY;
      
      // Prevent main content scrolling but allow modal content to scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        // Restore scrolling when component unmounts or modal closes
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Toggle expanded state for a feature
  const toggleFeature = (featureId) => {
    setExpandedFeatures(prev => ({
      ...prev,
      [featureId]: !prev[featureId]
    }));
  };

  // Modal animation variants
  const modalVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, transition: { duration: 0.2 } }
  };

  // Content animation variants
  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, delay: 0.1 } },
    exit: { opacity: 0, y: 20, transition: { duration: 0.2 } }
  };

  // Get the title based on active tab
  const getTitle = () => {
    switch (activeTab) {
      case 'router':
        return 'Free-to-Use Router Features';
      case 'isp':
        return 'ISP Service Features';
      case 'mesh':
        return 'Mesh WiFi Technology';
      default:
        return 'Features';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-30"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          {/* Modal - positioned below navigation */}
          <motion.div
            className="fixed top-16 inset-x-0 bottom-0 bg-gradient-to-b from-gray-900 to-gray-800 z-40 flex flex-col overflow-hidden"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Header */}
            <ModalHeader title={getTitle()} onClose={onClose} />
            
            {/* Tabs */}
            <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <AnimatePresence mode="wait">
                {activeTab === 'router' ? (
                  <RouterFeaturesContent contentVariants={contentVariants} />
                ) : activeTab === 'isp' ? (
                  <ISPFeaturesContent 
                    contentVariants={contentVariants} 
                    expandedFeatures={expandedFeatures} 
                    toggleFeature={toggleFeature} 
                  />
                ) : (
                  <MeshSystemContent contentVariants={contentVariants} />
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default RouterISPFeaturesModal;